/**
 * Financial calculation utilities for MJJ Trading Tool
 * Requirements: 5.4, 5.5, 5.6, 5.7
 */

import type { CardData, CalculationResult } from '../types/card';

/**
 * 計算剩餘天數
 * Property 1: 剩餘天數計算正確性
 * Validates: Requirements 5.4
 *
 * @param nextDueDate - 到期日 (YYYY-MM-DD 格式)
 * @param currentDate - 當前日期 (可選，預設為今天)
 * @returns 剩餘天數，最小為 0
 */
export function calculateRemainingDays(
  nextDueDate: string,
  currentDate: Date = new Date()
): number {
  if (!nextDueDate) return 0;

  const dueDate = new Date(nextDueDate);
  if (isNaN(dueDate.getTime())) return 0;

  // 將兩個日期都設為當天的開始時間以確保計算準確
  const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const current = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

  const diffTime = due.getTime() - current.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * 計算剩餘價值
 * Property 2: 剩餘價值計算正確性
 * Validates: Requirements 5.5
 *
 * @param renewalPrice - 續費價格
 * @param billingCycle - 計費週期天數 (0 = 終身)
 * @param remainingDays - 剩餘天數
 * @returns 剩餘價值
 */
export function calculateResidualValue(
  renewalPrice: number,
  billingCycle: number,
  remainingDays: number
): number {
  // 終身計費週期，無法計算剩餘價值
  if (billingCycle === 0) return 0;
  if (renewalPrice <= 0 || remainingDays <= 0) return 0;

  const dailyCost = renewalPrice / billingCycle;
  return dailyCost * remainingDays;
}

/**
 * 計算溢價/折價（使用目標幣種）
 * Property 3: 溢價/折價計算正確性
 * Validates: Requirements 5.6, 5.8, 5.9
 *
 * @param sellingPrice - 售價（目標幣種）
 * @param residualValueTarget - 剩餘價值（已轉換為目標幣種）
 * @returns { premium: 溢價/折價金額, isPremium: 是否為溢價 }
 */
export function calculatePremium(
  sellingPrice: number,
  residualValueTarget: number
): { premium: number; isPremium: boolean } {
  const premium = Math.abs(sellingPrice - residualValueTarget);
  const isPremium = sellingPrice >= residualValueTarget;

  return { premium, isPremium };
}

/**
 * 匯率轉換
 * Property 4: 匯率轉換正確性
 * Validates: Requirements 5.7
 *
 * @param amount - 金額
 * @param exchangeRate - 匯率
 * @returns 轉換後的金額（四捨五入到整數）
 */
export function convertCurrency(amount: number, exchangeRate: number): number {
  if (exchangeRate <= 0) return amount;
  return Math.round(amount * exchangeRate);
}

/**
 * 計算進度百分比
 *
 * @param remainingDays - 剩餘天數
 * @param billingCycle - 計費週期天數
 * @returns 進度百分比 (0-100)
 */
export function calculateProgress(remainingDays: number, billingCycle: number): number {
  if (billingCycle === 0) return 100; // 終身
  if (billingCycle <= 0) return 0;

  const progress = (remainingDays / billingCycle) * 100;
  return Math.min(100, Math.max(0, progress));
}

/**
 * 計算完整的財務結果
 *
 * @param cardData - 卡片資料
 * @param currentDate - 當前日期 (可選)
 * @returns 計算結果
 */
export function calculateFinance(
  cardData: CardData,
  currentDate: Date = new Date()
): CalculationResult {
  const remainingDays = calculateRemainingDays(cardData.nextDueDate, currentDate);
  const residualValue = calculateResidualValue(
    cardData.renewalPrice,
    cardData.billingCycle,
    remainingDays
  );
  
  // 將剩餘價值轉換為目標幣種
  const residualValueTarget = convertCurrency(residualValue, cardData.exchangeRate);
  
  // 售價已經是目標幣種，直接使用
  const sellingPriceTarget = cardData.sellingPrice;
  
  // 溢價/折價使用目標幣種計算
  const { premium, isPremium } = calculatePremium(sellingPriceTarget, residualValueTarget);
  const progress = calculateProgress(remainingDays, cardData.billingCycle);

  return {
    remainingDays,
    residualValue,
    residualValueTarget,
    sellingPriceTarget,
    premium,
    isPremium,
    progress,
  };
}
